// import { mockDeals } from "./__mocks__/deals";
import { generateDeals } from './__mocks__/dynamicDeals';

import { AFFILIATE_DEALS_PROXY_URL } from '../config';
import { withTokensIfNeeded } from '../utils/withTokensIfNeeded';
import { getLinkIdFromAffiliateUrl } from './getLinkIdFromAffiliateUrl';

interface DealAPIData {
    id: number;
    type: string;
    attributes: {
        link_id: number;
        brand_id: number;
        description: string;
        label: string;
        coupon_code: string | null;
        currency: string;
        starts_at: string;
        ends_at: string;
        featured_at: string | null;
        deactivated_at: string | null;
        url: string;
        created_at: string;
        updated_at: string;
        rank: string;
    };
    links: {
        self: string;
    };
    relations: {
        link: string;
    };
}

interface DealAPIResponse {
    data: DealAPIData[];
    links: {
        self: string;
        next: string;
    };
}

export interface DealUIObject {
    header: string;
    title: string;
    earnings: string;
    expirationMessage: string;
    ctaLabel: string;
    couponCode?: string;
    startAt: string;
    endAt: string;
    sortScore: number;
    url: string;
    dealType: 'deal' | 'coupon';
}

function mapDealsToUIObjects(deals: DealAPIData[]): DealUIObject[] {
    return deals.reduce<DealUIObject[]>((acc, deal) => {
        const isCoupon = !!deal.attributes.coupon_code;

        const endsAt = new Date(deal.attributes.ends_at).getTime();
        const now = new Date().getTime();
        const timeDiff = endsAt - now;

        if (timeDiff <= 0) {
            return acc;
        }

        let expirationMessage = '';
        if (timeDiff <= 24 * 60 * 60 * 1000) {
            expirationMessage = `Ends in ${Math.floor(timeDiff / (60 * 60 * 1000))} hours`;
        } else if (timeDiff <= 48 * 60 * 60 * 1000) {
            expirationMessage = 'Ends tomorrow';
        } else if (timeDiff <= 7 * 24 * 60 * 60 * 1000) {
            expirationMessage = `Ends in ${Math.floor(timeDiff / (24 * 60 * 60 * 1000))} days`;
        }

        const ctaLabel = isCoupon ? 'Shop Coupon' : 'Shop Deal';

        acc.push({
            header: isCoupon ? 'Online Coupon' : 'Online Deal',
            title: deal.attributes.label,
            earnings: '', // earningsText?
            expirationMessage,
            ctaLabel,
            couponCode: deal.attributes.coupon_code || undefined,
            startAt: deal.attributes.starts_at,
            endAt: deal.attributes.ends_at,
            sortScore: Number(deal.attributes.rank),
            url: deal.attributes.url,
            dealType: isCoupon ? 'coupon' : 'deal',
        });

        return acc;
    }, [] as DealUIObject[]);
}

export async function getDealsFromAffiliateUrl(affiliateUrl: string, token?: string): Promise<DealUIObject[]> {
    const linkId = getLinkIdFromAffiliateUrl(affiliateUrl);

    const deals = await getDealsByLinkId(linkId);
    deals.sort((a: DealUIObject, b: DealUIObject) => b.sortScore - a.sortScore);

    if (!token) {
        return deals;
    }

    return deals.map((deal) => {
        return {
            ...deal,
            url: (typeof deal.url === 'string') 
                ? deal.url.replace('{{token}}', token).replace('{{web}}', 'true') 
                : deal.url,
        }
    })
}

export async function getDealsByLinkId(linkId: string): Promise<DealUIObject[]> {
    const response = await withTokensIfNeeded<DealAPIResponse>(
        `${AFFILIATE_DEALS_PROXY_URL}&filters[link_id]=${linkId}&filters[ends_at_gt]=${Math.floor(Date.now() / 1000)}`,
        {
            method: 'GET',
            mode: 'cors',
        }
    );

    const deals = response.data?.data || [];
    return mapDealsToUIObjects(deals);

    /* enable this instead if you want mock data: */
    // const mockDeals = generateDeals(123);
    // return mapDealsToUIObjects(mockDeals);
}
